import styled from '@emotion/styled';
import {
  Box,
  Stack,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  SxProps,
  Typography,
  stepConnectorClasses,
  useTheme,
} from '@mui/material';
import { CustomStepperIcon } from 'Components/CustomStepperIcon';
import { useIsDesktop } from 'Hooks';

interface IStep {
  name: string;
  endDate?: string;
}

interface ICustomStepperWithLabels {
  steps: IStep[];
  currentStepIndex: number;
  textLabelComponent: React.ReactNode;
  stepperSx?: SxProps;
  currentStepLabelSx?: SxProps;
}

export const CustomStepperWithLabels = ({
  steps,
  currentStepIndex,
  textLabelComponent,
  stepperSx,
  currentStepLabelSx,
}: ICustomStepperWithLabels) => {
  const palette = useTheme().palette;
  const isDesktop = useIsDesktop();

  const CustomStepMobile = styled(StepConnector)(() => ({
    marginLeft: '11.5px',
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: palette.purple2.main,
        minHeight: '40px',
      },
    },
    [`&.${stepConnectorClasses.disabled}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        minHeight: '40px',
      },
    },
  }));

  const CustomStepDesktop = styled(StepConnector)(() => ({
    [`&.${stepConnectorClasses.disabled}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: palette.purple2.shade_200,
        height: '48px',
      },
    },
    [`&.${stepConnectorClasses.completed}`]: {
      [`& .${stepConnectorClasses.line}`]: {
        borderColor: palette.purple2.main,
      },
    },
  }));

  return (
    <Stepper
      alternativeLabel
      connector={isDesktop ? <CustomStepDesktop /> : <CustomStepMobile />}
      aria-hidden={'true'}
      orientation='vertical'
      sx={{ ...stepperSx }}
    >
      {steps.map((step: IStep, index: number) => (
        <Step
          key={index}
          completed={currentStepIndex >= index}
          sx={{
            width: '100%',
            '& .MuiStepConnector-root': { position: 'initial' },
          }}
          active={index === currentStepIndex}
        >
          <StepLabel
            StepIconComponent={CustomStepperIcon}
            sx={{
              display: 'flex',
              '&.MuiStepLabel-alternativeLabel': {
                flexDirection: 'row',
                alignItems: 'start',
              },
              '& .MuiStepLabel-label': {
                margin: '0 0 0 12px !important',
                width: 'fit-content',
                height: 'auto',
              },
            }}
          >
            <Stack direction='column' alignItems='start'>
              <Typography
                variant='body5'
                sx={
                  currentStepIndex === index
                    ? {
                        ...currentStepLabelSx,
                        p: '0 12px',
                        borderRadius: '5px',
                      }
                    : {}
                }
              >
                {step.name}
              </Typography>
              {currentStepIndex === index && (
                <Box
                  sx={
                    isDesktop
                      ? {}
                      : {
                          mt: '12px',
                          position: 'relative',
                        }
                  }
                >
                  {textLabelComponent}
                  {!isDesktop && currentStepIndex < index && (
                    <Box
                      sx={{
                        position: 'absolute',
                        height: '100%',
                        width: '1px',
                        background: palette.purple2.main,
                        top: '8px',
                        left: '-21px',
                      }}
                    ></Box>
                  )}
                </Box>
              )}
              {step.endDate && currentStepIndex > index && (
                <Typography variant='body6'>{step.endDate}</Typography>
              )}
            </Stack>
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
};
